import { useMsal } from '@azure/msal-react'
import {
  faBell,
  faBuilding,
  faBullhorn,
  faChartLine,
  faCreditCard,
  faHistory,
  faImage,
  faPaintBrush,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { usePendingApproval } from '../../contexts/PendingApprovalContext'
import { useSessionInfo } from '../../contexts/SessionContext'
import { OnboardingOrganization } from '../../models/Onboarding/OnboardingOrganization'
import { COLORS } from '../../utils/constants'
import { ModeToggleButton } from './ModeToggleButton'
import NavButton from './NavButton'
import SectionTitle from './SectionTitle'

interface DashboardMenuProps {
  organization: OnboardingOrganization | null
  onClose?: () => void
}

const DashboardMenu = ({ organization, onClose }: DashboardMenuProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { instance } = useMsal()
  const { setSession, session } = useSessionInfo()
  const { unreadCount } = useNotifications()
  const { pendingCount } = usePendingApproval()
  const [isHostMode, setIsHostMode, removeValue] = useLocalStorage({
    key: 'isHostMode',
    defaultValue: false,
  })

  const handleLinkClick = (path: string) => {
    navigate(`/dashboard/${path}`)
    onClose?.()
  }

  const handleSignOutClick = () => {
    setSession(null)
    removeValue()
    instance.logout().catch((error) => console.log(error))
    onClose?.()
  }

  const handleModeChange = (newMode: boolean) => {
    if (newMode !== isHostMode) {
      navigate('/dashboard')
    }
    setIsHostMode(newMode)
  }

  return (
    <div
      style={{
        backgroundColor: '#0B3B2D',
        width: '100%',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
      }}
    >
      <Stack justify='space-between' style={{ height: '100%' }}>
        <Stack>
          {/* Organization section - only visible on desktop */}
          <Box visibleFrom='sm'>
            <Stack bg={COLORS.LightGreen}>
              <Group h={70} px={24} gap={24}>
                <Stack gap={0}>
                  <Text c='white' fz={10}>
                    ORGANIZATION
                  </Text>
                  <Text c='white'>{organization?.name || 'Loading...'}</Text>
                </Stack>
                {session?.isHostOnboardingComplete && (
                  <ModeToggleButton
                    isHostMode={isHostMode}
                    setIsHostMode={handleModeChange}
                    pendingCount={pendingCount}
                  />
                )}
              </Group>
            </Stack>
          </Box>

          {/* Complete Business Profile button - only visible on mobile */}

          <Box hiddenFrom='sm' pt={24} px={24}>
            {!session?.isHostOnboardingComplete ? (
              <Button
                onClick={() => {
                  navigate('/hostOnboarding')
                  onClose?.()
                }}
                fullWidth
              >
                {session?.isHostOnboardingStarted
                  ? 'Complete Business Profile'
                  : 'Become a Host'}
              </Button>
            ) : (
              <ModeToggleButton
                isHostMode={isHostMode}
                setIsHostMode={handleModeChange}
                pendingCount={pendingCount}
              />
            )}
          </Box>

          <Stack px={24} pb={{ base: 80, sm: 24 }} pt={{ base: 24, sm: 0 }}>
            {/* Notifications Section */}
            <Tooltip
              label='View and manage your notifications, including ad approvals, payment confirmations, and system updates'
              position='right'
              withArrow
              openDelay={500}
            >
              <div>
                <NavButton
                  icon={<FontAwesomeIcon icon={faBell} width={18} />}
                  label='Notifications'
                  active={location.pathname === '/dashboard/notifications'}
                  onClick={() => handleLinkClick('notifications')}
                  badge={unreadCount}
                />
              </div>
            </Tooltip>

            {/* Advertisements Section */}
            <SectionTitle title='Advertisements' />
            {!isHostMode && (
              <>
                <Tooltip
                  label='Manage your active advertisements, view performance metrics, and track your ad campaigns'
                  position='right'
                  withArrow
                  openDelay={500}
                >
                  <div>
                    <NavButton
                      icon={<FontAwesomeIcon icon={faBullhorn} width={18} />}
                      label='My Advertisements'
                      active={location.pathname === '/dashboard/myAds'}
                      onClick={() => handleLinkClick('myAds')}
                    />
                  </div>
                </Tooltip>
                <Tooltip
                  label='View impressions and QR scan metrics across all your advertisements'
                  position='right'
                  withArrow
                  openDelay={500}
                >
                  <div>
                    <NavButton
                      icon={<FontAwesomeIcon icon={faChartLine} width={18} />}
                      label='Metrics'
                      active={location.pathname === '/dashboard/metrics'}
                      onClick={() => handleLinkClick('metrics')}
                    />
                  </div>
                </Tooltip>
                <Tooltip
                  label='Request custom ad designs from our team, track design progress, and communicate with designers'
                  position='right'
                  withArrow
                  openDelay={500}
                >
                  <div>
                    <NavButton
                      icon={<FontAwesomeIcon icon={faPaintBrush} width={18} />}
                      label='Design Requests'
                      active={location.pathname === '/dashboard/designRequests'}
                      onClick={() => handleLinkClick('designRequests')}
                    />
                  </div>
                </Tooltip>
              </>
            )}
            {isHostMode && session?.isHostOnboardingComplete && (
              <Tooltip
                label='Review and approve advertisements submitted for your locations, manage ad content and scheduling'
                position='right'
                withArrow
                openDelay={500}
              >
                <div>
                  <NavButton
                    icon={<FontAwesomeIcon icon={faImage} width={18} />}
                    label='Ads at my Locations'
                    active={location.pathname === '/dashboard/locationAds'}
                    onClick={() => handleLinkClick('locationAds')}
                    badge={pendingCount}
                  />
                </div>
              </Tooltip>
            )}
            {/* Organization Management Section (Host mode only) */}
            {isHostMode && session?.isHostOnboardingComplete && (
              <>
                <SectionTitle title='Organization Management' />
                <Tooltip
                  label='Add, edit, and manage your business locations, update device information and pricing'
                  position='right'
                  withArrow
                  openDelay={500}
                >
                  <div>
                    <NavButton
                      icon={<FontAwesomeIcon icon={faBuilding} width={18} />}
                      label='Manage Locations'
                      active={location.pathname === '/dashboard/locations'}
                      onClick={() => handleLinkClick('locations')}
                    />
                  </div>
                </Tooltip>
              </>
            )}
            {/* Financials Section (Always visible) */}
            <SectionTitle title='Financials' />
            {isHostMode && session?.isHostOnboardingComplete && (
              <Tooltip
                label='Set up your Stripe Connect account, manage banking information, and configure payout settings'
                position='right'
                withArrow
                openDelay={500}
              >
                <div>
                  <NavButton
                    icon={<FontAwesomeIcon icon={faCreditCard} width={18} />}
                    label='Billing & Banking'
                    active={location.pathname === '/dashboard/billing'}
                    onClick={() => handleLinkClick('billing')}
                  />
                </div>
              </Tooltip>
            )}
            {!isHostMode && (
              <Tooltip
                label='View your payment history, download invoices, and track your advertising expenses'
                position='right'
                withArrow
                openDelay={500}
              >
                <div>
                  <NavButton
                    icon={<FontAwesomeIcon icon={faHistory} width={18} />}
                    label='Transaction History'
                    active={location.pathname.startsWith(
                      '/dashboard/transactions',
                    )}
                    onClick={() => handleLinkClick('transactions')}
                  />
                </div>
              </Tooltip>
            )}
            {/* Earnings link, only if onboarding complete */}
            {isHostMode && session?.isHostOnboardingComplete && (
              <Tooltip
                label='Track your earnings from advertisements displayed at your locations, view payout history and analytics'
                position='right'
                withArrow
                openDelay={500}
              >
                <div>
                  <NavButton
                    icon={<FontAwesomeIcon icon={faChartLine} width={18} />}
                    label='My Earnings'
                    active={location.pathname === '/dashboard/hostDashboard'}
                    onClick={() => handleLinkClick('hostDashboard')}
                  />
                </div>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Sign out button - fixed on mobile, normal on desktop */}
        <Box
          hiddenFrom='sm'
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#0B3B2D',
            padding: '16px 24px',
            borderTop: `1px solid ${COLORS.LightGreen}`,
          }}
        >
          <Text c={'white'} ta={'center'} mb={16}>
            Signed in as <br />
            {session?.user.email}
          </Text>
          <Button
            variant='transparent'
            color='white'
            leftSection={<FontAwesomeIcon icon={faSignOutAlt} width={18} />}
            onClick={handleSignOutClick}
            style={{ borderColor: COLORS.LightGreen }}
            fullWidth
          >
            Sign Out
          </Button>
        </Box>

        {/* Sign out button for desktop */}
        <Box visibleFrom='sm' p={24}>
          <Text c={'white'} ta={'center'} mb={16}>
            Signed in as <br />
            {session?.user.email}
          </Text>
          <Group justify='center'>
            <Button
              variant='transparent'
              color='white'
              leftSection={<FontAwesomeIcon icon={faSignOutAlt} width={18} />}
              onClick={handleSignOutClick}
              style={{ borderColor: COLORS.LightGreen }}
            >
              Sign Out
            </Button>
          </Group>
        </Box>
      </Stack>
    </div>
  )
}

export default DashboardMenu
